import React from 'react';
import { useAtom } from 'jotai';
import { LinkOverlay, LinkOverlayContainer, Heading, Text, HeadingVariant, Pill } from '@web-for-marketing/react-ui';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { BlogCardImage, BlogPill } from '@models/blogCards';
import { blogCardStyle as classes } from './BlogCardStyle';
import { blogCard1Styles as blog1Classes } from './BlogCard1Style';
import { DateAndReadTime } from './DateAndReadTime';

export interface BlogCard1Props {
    title: string;
    body?: string;
    linkPath: string;
    ariaLabel?: string;
    image: BlogCardImage;
    pillData?: BlogPill;
    publishedDateUtc: string;
    readMinutes?: number;
    className?: string;
    lazyLoad?: boolean;
    headingLevel?: HeadingVariant;
}

export function BlogCard1({
    title,
    body,
    linkPath,
    ariaLabel,
    image,
    pillData,
    publishedDateUtc,
    readMinutes,
    className,
    lazyLoad = false,
    headingLevel,
}: BlogCard1Props): React.ReactElement {
    const [selectedLanguage] = useAtom(selectedLanguageAtom);

    return (
        <LinkOverlayContainer css={blog1Classes.cardContainer} className={className}>
            <div css={blog1Classes.picture}>
                <img
                    css={classes.image}
                    src={!lazyLoad ? image.imagePath : undefined}
                    data-src={lazyLoad ? image.imagePath : undefined}
                    alt={image.imageAlt}
                />
            </div>
            <div css={classes.cardContent}>
                {pillData ? (
                    <Pill
                        css={blog1Classes.categoryMargins}
                        renderLink={pillData.link ? <a href={pillData.link}>{pillData.name}</a> : undefined}
                        variant='secondaryOutline'
                    >
                        {!pillData.link ? pillData.name : undefined}
                    </Pill>
                ) : null}
                <Heading
                    variant='h4'
                    component={headingLevel ?? 'h3'}
                    weight='bold'
                    css={blog1Classes.title(!!pillData)}
                >
                    <LinkOverlay href={linkPath} aria-label={ariaLabel} css={blog1Classes.textTruncate}>
                        {title}
                    </LinkOverlay>
                </Heading>
                {body ? (
                    <Text
                        variant='body2'
                        css={[classes.description, blog1Classes.hideOnMobile, blog1Classes.textTruncateDescription]}
                        dangerouslySetInnerHTML={{ __html: body }}
                    />
                ) : null}
                <DateAndReadTime
                    publishedDateUtc={publishedDateUtc}
                    selectedLanguage={selectedLanguage}
                    readMinutes={readMinutes}
                />
            </div>
        </LinkOverlayContainer>
    );
}
